import "./styles.css";

import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";

// The type of messages we'll be receiving from the server
import type { OutgoingMessage } from "../shared";
import type { LegacyRef } from "react";

function App() {
  // A reference to the canvas element where we'll render the globe
  const canvasRef = useRef<HTMLCanvasElement>();
  // The number of markers we're currently displaying
  const [counter, setCounter] = useState(0);
  // A map of marker IDs to their positions
  // Note that we use a ref because the globe's `onRender` callback
  // is called on every animation frame, and we don't want to re-render
  // the component on every frame.
  const positions = useRef<
    Map<
      string,
      {
        location: [number, number];
        size: number;
      }
    >
  >(new Map());
  
  // Connect to the PartyServer server
  const socket = usePartySocket({
    room: "default",
    party: "globe",
    onMessage(evt) {
      const message = JSON.parse(evt.data as string) as OutgoingMessage;
      if (message.type === "add-marker") {
        // Add the marker to our map
        positions.current.set(message.position.id, {
          location: [message.position.lat, message.position.lng],
          size: message.position.id === socket.id ? 0.1 : 0.05,
        });
        // Update the counter
        setCounter((c) => c + 1);
      } else {
        // Remove the marker from our map
        positions.current.delete(message.id);
        // Update the counter
        setCounter((c) => c - 1);
      }
    },
  });

  useEffect(() => {
    // 确保canvas元素初始化正确
    if (canvasRef.current) {
      // 设置canvas实际宽高与样式一致
      canvasRef.current.width = 600;
      canvasRef.current.height = 600;
    }
    
    // The angle of rotation of the globe
    // We'll update this on every frame to make the globe spin
    let phi = 0;

    const globe = createGlobe(canvasRef.current as HTMLCanvasElement, {
      devicePixelRatio: 1, // 固定设备像素比
      width: 600, // 保持一致的宽度
      height: 600, // 保持一致的高度
      phi: 0,
      theta: -0.2,   // 使用负值调整视角，避免右上角偏移
      dark: 1,
      diffuse: 1.2,
      mapSamples: 25000,
      mapBrightness: 8,
      baseColor: [0.1, 0.15, 0.2],
      markerColor: [0.3, 0.8, 0.6],
      glowColor: [0.2, 0.8, 0.8],
      scale: 1.0,  // 进一步缩小比例，确保完整显示
      markers: [],
      opacity: 0.9,
      onRender: (state) => {
        // Called on every animation frame.
        // `state` will be an empty object, return updated params.

        // Get the current positions from our map
        state.markers = [...positions.current.values()];

        // Rotate the globe
        state.phi = phi;
        phi += 0.005;
      },
    });

    return () => {
      globe.destroy();
    };
  }, []);

  return (
    <div className="App">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>量科智视 · AI 赋能教育 · 让学习无界</h1>
          <p>以大数据驱动个性化学习，用 AI 架起全球课堂</p>
          <div>
            <a href="#contact" className="btn">免费体验</a>
            <a href="#about" className="btn btn-outline">了解更多</a>
          </div>
        </div>
        
        <div className="globe-container">
          <div className="glow"></div>
          <div className="globe-wrapper">
            <canvas
              ref={canvasRef as LegacyRef<HTMLCanvasElement>}
              width={600}
              height={600}
              style={{ width: 600, height: 600, maxWidth: "100%", aspectRatio: 1 }}
            />
            {counter !== 0 && (
              <p className="connected-info">
                <b>{counter}</b> {counter === 1 ? "person" : "people"} connected.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* AI Engine Section */}
      <section className="section section-alt" id="ai-engine">
        <div className="container">
          <h2>我们的 AI 引擎</h2>
          <p>量科智视的人工智能技术核心，为每一位学习者提供个性化学习体验</p>
          
          <div className="ai-engine">
            <div className="ai-card">
              <i className="fas fa-brain product-icon"></i>
              <h3>自然语言处理</h3>
              <p>通过深度学习模型，精准理解学生答案，实现语义级智能批改和反馈</p>
            </div>
            <div className="ai-card">
              <i className="fas fa-project-diagram product-icon"></i>
              <h3>知识图谱</h3>
              <p>构建全面的学科知识网络，精确定位学习弱点，个性化推荐学习内容</p>
            </div>
            <div className="ai-card">
              <i className="fas fa-chart-line product-icon"></i>
              <h3>自适应学习</h3>
              <p>基于实时学习数据，动态调整学习路径，提供最适合的学习挑战</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section" id="products">
        <div className="container">
          <h2>产品与场景</h2>
          <p>全方位覆盖教学全流程，助力教育机构和学生实现高效学习</p>
          
          <div className="products">
            <div className="product-card">
              <i className="fas fa-check-double product-icon"></i>
              <h3>智能批改平台</h3>
              <p>自动评估学生作业，提供精准反馈，节省教师时间，提高批改效率</p>
            </div>
            <div className="product-card">
              <i className="fas fa-video product-icon"></i>
              <h3>实时在线辅导</h3>
              <p>AI助教全天候在线，即时解答问题，提供个性化的学习支持</p>
            </div>
            <div className="product-card">
              <i className="fas fa-road product-icon"></i>
              <h3>自适应学习路径</h3>
              <p>根据学生表现定制学习计划，确保每位学生都能达到最佳学习效果</p>
            </div>
            <div className="product-card">
              <i className="fas fa-chart-pie product-icon"></i>
              <h3>数据可视化仪表盘</h3>
              <p>全面展示学习进度和表现，帮助教师和家长及时掌握学生学习情况</p>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="section section-alt" id="partners">
        <div className="container">
          <h2>成功案例 & 合作伙伴</h2>
          <p>与行业领先机构合作，共同推动教育创新</p>
          
          <div className="partners">
            <a href="#" className="partner-logo" target="_blank" rel="noopener noreferrer">
              <img src="/images/partners/partner-1.png" alt="合作伙伴1" />
            </a>
            <a href="#" className="partner-logo" target="_blank" rel="noopener noreferrer">
              <img src="/images/partners/partner-2.png" alt="合作伙伴2" />
            </a>
            <a href="#" className="partner-logo" target="_blank" rel="noopener noreferrer">
              <img src="/images/partners/partner-3.png" alt="合作伙伴3" />
            </a>
            <a href="#" className="partner-logo" target="_blank" rel="noopener noreferrer">
              <img src="/images/partners/partner-4.png" alt="合作伙伴4" />
            </a>
            <a href="#" className="partner-logo" target="_blank" rel="noopener noreferrer">
              <img src="/images/partners/partner-5.png" alt="合作伙伴5" />
            </a>
          </div>
          
          <div className="partner-info">
            <p>想要了解更多合作机会？ <a href="#contact" className="partner-cta">联系我们</a></p>
          </div>
        </div>
      </section>


      {/* Team Section */}
            {/* 
      <section className="section" id="team">
        <div className="container">
          <h2>师资 & 团队</h2>
          <p>汇聚教育专家和AI精英，打造智能教育新时代</p>
          
          <div className="team">
            <div className="team-member">
              <div className="team-avatar">专家 1</div>
              <h3>张教授</h3>
              <p>教育学博士，十年教学经验</p>
            </div>
            <div className="team-member">
              <div className="team-avatar">专家 2</div>
              <h3>李博士</h3>
              <p>AI研究员，前Google科学家</p>
            </div>
            <div className="team-member">
              <div className="team-avatar">专家 3</div>
              <h3>王工程师</h3>
              <p>资深全栈开发，教育科技专家</p>
            </div>
            <div className="team-member">
              <div className="team-avatar">专家 4</div>
              <h3>赵设计师</h3>
              <p>用户体验专家，教育产品设计师</p>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Contact Section */}
      <footer id="contact">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <h4>联系我们</h4>
              <p><i className="fas fa-map-marker-alt"></i> 地址：</p>
              <p><i className="fas fa-envelope"></i> 邮箱：hy-si@foxmail.com</p>
              <p><i className="fas fa-phone"></i> 电话：</p>
            </div>
            <div className="footer-column">
              <h4>关注我们</h4>
              <p><i className="fab fa-weixin"></i> 微信公众号：</p>
              <p><i className="fab fa-weibo"></i> 微博：</p>
              <p><i className="fab fa-zhihu"></i> 知乎: </p>
            </div>
            <div className="footer-column">
              <h4>快速链接</h4>
              <p><a href="#ai-engine">AI引擎</a></p>
              <p><a href="#products">产品与场景</a></p>
              <p><a href="#partners">合作伙伴</a></p>
              {/* <p><a href="#team">团队介绍</a></p> */}
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 量科智视 (Quavic Tech). 保留所有权利。</p>
            <p className="credits">
              Powered by <a href="https://dash.cloudflare.com/">🌏 Cloudflare</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<App />);
